// Student CoursesPage — course catalog with an Enrol action per card. Enrolled cards show their payment
// status and, while payment is outstanding, expand the M-Pesa instructions right below the card.
import { Fragment, useMemo, useState } from "react";
import { useFetch, postJson } from "../../hooks/useApi.js";
import { useCourses } from "../../contexts/CoursesContext.js";
import Badge from "../../components/shared/Badge.js";
import EnrollmentPaymentNotice from "../../components/student/EnrollmentPaymentNotice.js";
import MpesaInstructions from "../../components/student/MpesaInstructions.js";
import { resolveCourseAccountRef } from "@dm-lms/shared/utils";
import type { EnrollmentCreated, EnrollmentWithPayment, PaginatedResult } from "@dm-lms/shared/types";

const SKELETON_COUNT = 6;

export default function CoursesPage() {
  const { courses, isLoading, error } = useCourses();
  const { data: enrollmentsData, refresh: refreshEnrollments } = useFetch<PaginatedResult<EnrollmentWithPayment>>(
    "/api/enrollments/mine?page=1&pageSize=100"
  );

  // Each enrolled course's enrollment, carrying its latest payment
  const enrollmentByCourseId = useMemo(
    () => new Map((enrollmentsData?.items ?? []).map((enrollment) => [enrollment.courseId, enrollment])),
    [enrollmentsData]
  );

  const [enrollingId, setEnrollingId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expandedCourseId, setExpandedCourseId] = useState<string | null>(null);   // one card's instructions at a time

  async function enrol(courseId: string) {
    setEnrollingId(courseId);
    setErrors((prev) => ({ ...prev, [courseId]: "" }));

    const result = await postJson<EnrollmentCreated>("/api/enrollments", { courseId });
    if (!result.ok) {
      setEnrollingId(null);
      setErrors((prev) => ({ ...prev, [courseId]: result.error }));
      return;
    }

    // Wait for the card to gain its payment data before opening the instructions on it
    await refreshEnrollments();
    setExpandedCourseId(courseId);
    setEnrollingId(null);
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Courses</h1>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div key={i} className="rounded-lg border border-gray-200 bg-white p-5 animate-pulse">
              <div className="h-5 w-3/4 bg-gray-200 rounded mb-3" />
              <div className="h-4 w-16 bg-gray-200 rounded-full mb-4" />
              <div className="h-4 w-1/2 bg-gray-200 rounded mb-2" />
              <div className="h-4 w-1/3 bg-gray-200 rounded mb-4" />
              <div className="h-9 w-full bg-gray-200 rounded-md" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && !error && (
        courses.length === 0 ? (
          <p className="text-dm-slate text-sm">No courses are open for enrollment right now.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((course) => {
              const enrollment = enrollmentByCourseId.get(course.id);
              const payment = enrollment?.payment ?? null;
              const isEnrolling = enrollingId === course.id;
              const courseError = errors[course.id];
              const isExpanded = expandedCourseId === course.id;
              const showInstructions =
                isExpanded && payment !== null && (payment.status === "pending" || payment.status === "failed");

              return (
                <Fragment key={course.id}>
                  <div className="rounded-lg border border-gray-200 bg-white p-5 flex flex-col">
                    <h2 className="font-medium text-dm-navy mb-2">{course.title}</h2>
                    <div className="mb-4">
                      <Badge tone="gray">{course.mode}</Badge>
                    </div>
                    <p className="text-sm text-dm-slate mb-1">{course.durationHours} hours</p>
                    <p className="text-sm text-dm-slate mb-4">KES {course.priceKes?.toLocaleString() ?? "—"}</p>

                    <div className="mt-auto">
                      {enrollment ? (
                        <>
                          <Badge tone="green">Enrolled</Badge>
                          <EnrollmentPaymentNotice
                            payment={payment}
                            expanded={isExpanded}
                            onToggle={() => { setExpandedCourseId(isExpanded ? null : course.id); }}
                          />
                        </>
                      ) : (
                        <button
                          type="button"
                          className="w-full bg-dm-gold text-dm-navy text-sm font-medium px-4 py-2 rounded-md disabled:opacity-50"
                          disabled={isEnrolling}
                          onClick={() => enrol(course.id)}
                        >
                          {isEnrolling ? "Enrolling…" : "Enrol"}
                        </button>
                      )}
                      {courseError && <p className="text-red-600 text-xs mt-2">{courseError}</p>}
                    </div>
                  </div>

                  {showInstructions && (
                    <div className="col-span-full">
                      <MpesaInstructions
                        paymentId={payment.id}
                        initialReference={payment.reference}
                        verificationFailed={payment.status === "failed"}
                        amount={payment.amountKes}
                        paymentCode={payment.code}
                        courseAccountRef={resolveCourseAccountRef(course)}
                        onSubmitted={() => { void refreshEnrollments(); }}
                        onDismiss={() => { setExpandedCourseId(null); }}
                      />
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
